import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { orgChartAPI } from '../lib/api';
import { 
  Building2, 
  MapPin, 
  Video, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw,
  Upload,
  Trash2,
  FileText,
  File,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// Image viewer component with zoom controls (also handles PDF via iframe)
const OrgChartImage = ({ src, alt, isDark }) => {
  const [scale, setScale] = useState(1);
  const [error, setError] = useState(false);
  
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setScale(1);

  const isPdf = typeof src === 'string' && src.toLowerCase().split('?')[0].endsWith('.pdf');

  if (error) {
    return (
      <div className={cn(
        "flex flex-col items-center justify-center p-8 rounded-lg border",
        isDark ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"
      )}>
        <AlertCircle className="w-12 h-12 text-slate-400 mb-2" />
        <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
          Image not available
        </p>
      </div>
    );
  }

  if (isPdf) {
    return (
      <div className={cn(
        "rounded-lg border overflow-hidden",
        isDark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
      )}>
        <iframe
          src={`${src}#toolbar=1&navpanes=0&view=FitH`}
          title={alt}
          className="w-full"
          style={{ height: '78vh', border: 0 }}
          data-testid={`org-chart-pdf-${alt.toLowerCase().replace(/\s+/g, '-')}`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Zoom Controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={zoomOut}
          className={cn(
            "p-2 rounded-lg transition-colors",
            isDark 
              ? "bg-slate-700 hover:bg-slate-600 text-slate-200" 
              : "bg-slate-100 hover:bg-slate-200 text-slate-700"
          )}
          title="Zoom Out"
          data-testid="zoom-out-btn"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className={cn(
          "text-sm font-medium min-w-[60px] text-center",
          isDark ? "text-slate-300" : "text-slate-600"
        )}>
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={zoomIn}
          className={cn(
            "p-2 rounded-lg transition-colors",
            isDark 
              ? "bg-slate-700 hover:bg-slate-600 text-slate-200" 
              : "bg-slate-100 hover:bg-slate-200 text-slate-700"
          )}
          title="Zoom In"
          data-testid="zoom-in-btn"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          onClick={resetZoom}
          className={cn(
            "p-2 rounded-lg transition-colors",
            isDark 
              ? "bg-slate-700 hover:bg-slate-600 text-slate-200" 
              : "bg-slate-100 hover:bg-slate-200 text-slate-700"
          )}
          title="Reset Zoom"
          data-testid="reset-zoom-btn"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
      
      {/* Image Container with scroll */}
      <div 
        className={cn(
          "overflow-auto rounded-lg border max-h-[70vh]",
          isDark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
        )}
      >
        <img
          src={src}
          alt={alt}
          className="transition-transform duration-200"
          style={{ 
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            minWidth: scale > 1 ? `${scale * 100}%` : '100%'
          }}
          onError={() => setError(true)}
          data-testid={`org-chart-image-${alt.toLowerCase().replace(/\s+/g, '-')}`}
        />
      </div>
    </div>
  );
};

// Multi-page image viewer
const MultiPageViewer = ({ images, title, isDark }) => {
  const [currentPage, setCurrentPage] = useState(0);
  
  if (!images || images.length === 0) {
    return (
      <div className={cn(
        "flex flex-col items-center justify-center p-12 rounded-lg border",
        isDark ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"
      )}>
        <FileText className="w-16 h-16 text-slate-300 mb-3" />
        <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
          No org chart uploaded for {title}
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Page navigation if multiple pages */}
      {images.length > 1 && (
        <div className="flex items-center gap-2">
          <span className={cn(
            "text-sm font-medium",
            isDark ? "text-slate-300" : "text-slate-600"
          )}>
            Page {currentPage + 1} of {images.length}
          </span>
          <div className="flex gap-1">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentPage(idx)}
                className={cn(
                  "w-8 h-8 rounded-lg text-sm font-medium transition-colors",
                  currentPage === idx
                    ? "bg-[#331062] text-white"
                    : isDark
                      ? "bg-slate-700 text-slate-300 hover:bg-slate-600"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                )}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <OrgChartImage 
        src={images[currentPage]} 
        alt={`${title} Page ${currentPage + 1}`}
        isDark={isDark}
      />
    </div>
  );
};

// File upload component
const FileUploadSection = ({ category, onUpload, isDark }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      await onUpload(file, category);
      toast.success(`File uploaded successfully for ${category}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={cn(
      "p-4 rounded-lg border-2 border-dashed",
      isDark ? "border-slate-600 bg-slate-800/30" : "border-slate-300 bg-slate-50"
    )}>
      <input
        type="file"
        ref={fileInputRef}
        accept=".pdf,.ppt,.pptx"
        onChange={handleFileSelect}
        className="hidden"
        data-testid={`upload-input-${category}`}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Upload className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
          <div>
            <p className={cn("text-sm font-medium", isDark ? "text-slate-200" : "text-slate-700")}>
              Upload Org Chart
            </p>
            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
              PDF or PowerPoint files accepted
            </p>
          </div>
        </div>
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          size="sm"
          data-testid={`upload-btn-${category}`}
          className="bg-[#331062] hover:bg-[#4a1d8a]"
        >
          {uploading ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              Uploading...
            </span>
          ) : (
            'Choose File'
          )}
        </Button>
      </div>
    </div>
  );
};

// File list component
const FileList = ({ files, onDelete, isDark, isAdmin }) => {
  if (!files || files.length === 0) return null;

  return (
    <div className="mt-4 space-y-2">
      <p className={cn("text-xs font-medium", isDark ? "text-slate-400" : "text-slate-500")}>
        Uploaded Files:
      </p>
      {files.map((file) => (
        <div 
          key={file.filename}
          className={cn(
            "flex items-center justify-between p-2 rounded-lg",
            isDark ? "bg-slate-700/50" : "bg-slate-100"
          )}
        >
          <div className="flex items-center gap-2">
            <File className={cn("w-4 h-4", isDark ? "text-slate-400" : "text-slate-500")} />
            <span className={cn("text-sm", isDark ? "text-slate-200" : "text-slate-700")}>
              {file.filename}
            </span>
            <span className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
              ({(file.size / 1024).toFixed(1)} KB)
            </span>
          </div>
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(file.filename)}
              className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              data-testid={`delete-file-${file.filename}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

const OrgChart = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [charts, setCharts] = useState({ noida: [], kolkata: [], motion: [], other: [] });
  const [loading, setLoading] = useState(true);
  
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCharts();
  }, []);

  const fetchCharts = async () => {
    try {
      setLoading(true);
      const response = await orgChartAPI.getAll();
      setCharts(response.data);
    } catch (error) {
      console.error('Error fetching org charts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file, category) => {
    await orgChartAPI.upload(file, category);
    await fetchCharts();
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) return;
    
    try {
      await orgChartAPI.delete(filename);
      toast.success('File deleted successfully');
      await fetchCharts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    }
  };

  // Get PNG images for display (filter to show only images, preferring PNG)
  const getDisplayImages = (files) => {
    const pngFiles = files.filter(f => f.type === '.png');
    if (pngFiles.length > 0) {
      return pngFiles.map(f => `${backendUrl}${f.url}`);
    }
    // If no PNGs, show PDFs in iframe or fallback
    const pdfFiles = files.filter(f => f.type === '.pdf');
    if (pdfFiles.length > 0) {
      return pdfFiles.map(f => `${backendUrl}${f.url}`);
    }
    return [];
  };

  // Fallback to static images if no dynamic ones
  const noidaImages = getDisplayImages(charts.noida).length > 0 
    ? getDisplayImages(charts.noida) 
    : ['/org_charts/noida-1.png', '/org_charts/noida-2.png'];
    
  const kolkataImages = getDisplayImages(charts.kolkata).length > 0 
    ? getDisplayImages(charts.kolkata) 
    : ['/org_charts/kolkata-1.png', '/org_charts/kolkata-2.png', '/org_charts/kolkata-3.png'];
    
  const motionImages = getDisplayImages(charts.motion).length > 0 
    ? getDisplayImages(charts.motion) 
    : ['/org_charts/motion-1.png', '/org_charts/motion-2.png'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="org-chart-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn(
            "text-2xl font-bold font-outfit flex items-center gap-2",
            isDark ? "text-white" : "text-slate-800"
          )}>
            <Building2 className="w-6 h-6 text-[#331062]" />
            Organization Chart
          </h1>
          <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            Team structure and reporting hierarchy
          </p>
        </div>
        {isAdmin && (
          <div className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium",
            isDark ? "bg-green-900/30 text-green-300" : "bg-green-100 text-green-700"
          )}>
            <CheckCircle className="w-3.5 h-3.5" />
            Admin Mode - Upload/Delete Enabled
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="noida" className="w-full">
        <TabsList className="mb-3">
          <TabsTrigger value="noida" className="flex items-center gap-1.5 text-sm" data-testid="tab-noida">
            <MapPin className="w-3.5 h-3.5" /> Noida
          </TabsTrigger>
          <TabsTrigger value="kolkata" className="flex items-center gap-1.5 text-sm" data-testid="tab-kolkata">
            <MapPin className="w-3.5 h-3.5" /> Kolkata
          </TabsTrigger>
          <TabsTrigger value="motion" className="flex items-center gap-1.5 text-sm" data-testid="tab-motion">
            <Video className="w-3.5 h-3.5" /> Motion
          </TabsTrigger>
        </TabsList>

        <TabsContent value="noida">
          <Card className={cn("p-4", isDark ? "bg-slate-800/50 border-slate-700" : "")}>
            {/* Chart Preview - At the Top */}
            <div className="mb-6">
              <h3 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", isDark ? "text-white" : "text-slate-700")}>
                <MapPin className="w-5 h-5 text-[#331062]" />
                Noida Team Structure
              </h3>
              <MultiPageViewer images={noidaImages} title="Noida" isDark={isDark} />
            </div>
            
            {/* Admin Controls - Below the Chart */}
            {isAdmin && (
              <div className={cn(
                "mt-6 pt-6 border-t",
                isDark ? "border-slate-700" : "border-slate-200"
              )}>
                <h4 className={cn("text-sm font-medium mb-3 flex items-center gap-2", isDark ? "text-slate-300" : "text-slate-600")}>
                  <Upload className="w-4 h-4" />
                  Manage Files
                </h4>
                <FileUploadSection category="noida" onUpload={handleUpload} isDark={isDark} />
                <FileList files={charts.noida} onDelete={handleDelete} isDark={isDark} isAdmin={isAdmin} />
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="kolkata">
          <Card className={cn("p-4", isDark ? "bg-slate-800/50 border-slate-700" : "")}>
            {/* Chart Preview - At the Top */}
            <div className="mb-6">
              <h3 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", isDark ? "text-white" : "text-slate-700")}>
                <MapPin className="w-5 h-5 text-[#331062]" />
                Kolkata Team Structure
              </h3>
              <MultiPageViewer images={kolkataImages} title="Kolkata" isDark={isDark} />
            </div>
            
            {/* Admin Controls - Below the Chart */}
            {isAdmin && (
              <div className={cn(
                "mt-6 pt-6 border-t",
                isDark ? "border-slate-700" : "border-slate-200"
              )}>
                <h4 className={cn("text-sm font-medium mb-3 flex items-center gap-2", isDark ? "text-slate-300" : "text-slate-600")}>
                  <Upload className="w-4 h-4" />
                  Manage Files
                </h4>
                <FileUploadSection category="kolkata" onUpload={handleUpload} isDark={isDark} />
                <FileList files={charts.kolkata} onDelete={handleDelete} isDark={isDark} isAdmin={isAdmin} />
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="motion">
          <Card className={cn("p-4", isDark ? "bg-slate-800/50 border-slate-700" : "")}>
            {/* Chart Preview - At the Top */}
            <div className="mb-6">
              <h3 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", isDark ? "text-white" : "text-slate-700")}>
                <Video className="w-5 h-5 text-[#331062]" />
                Motion Team Structure
              </h3>
              <MultiPageViewer images={motionImages} title="Motion" isDark={isDark} />
            </div>
            
            {/* Admin Controls - Below the Chart */}
            {isAdmin && (
              <div className={cn(
                "mt-6 pt-6 border-t",
                isDark ? "border-slate-700" : "border-slate-200"
              )}>
                <h4 className={cn("text-sm font-medium mb-3 flex items-center gap-2", isDark ? "text-slate-300" : "text-slate-600")}>
                  <Upload className="w-4 h-4" />
                  Manage Files
                </h4>
                <FileUploadSection category="motion" onUpload={handleUpload} isDark={isDark} />
                <FileList files={charts.motion} onDelete={handleDelete} isDark={isDark} isAdmin={isAdmin} />
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrgChart;
